"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Shield, ChevronDown, ChevronUp } from "lucide-react";
import Link from "next/link";
import { 
  getStoredConsent as getStoredConsentUtil, 
  setStoredConsent as setStoredConsentUtil,
  type CookieConsentPreferences 
} from "@/lib/cookieConsent";

// Re-export for backwards compatibility
const COOKIE_CONSENT_KEY = "replenico_cookie_consent";
const COOKIE_CONSENT_VERSION = "1.0";

interface CookieConsent {
  essential: boolean;
  analytics: boolean;
  preferences: boolean;
  marketing: boolean;
  timestamp: string;
  version: string;
}

const getStoredConsent = (): CookieConsent | null => {
  return getStoredConsentUtil() as CookieConsent | null;
};

const setStoredConsent = (consent: CookieConsent) => {
  setStoredConsentUtil(consent as CookieConsentPreferences);
};

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: true,
    preferences: true,
    marketing: false,
  });

  useEffect(() => {
    // Check if consent has already been given
    const stored = getStoredConsent();
    if (!stored) {
      // Small delay before showing for smoother UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent: CookieConsent = {
      essential: true,
      analytics: true,
      preferences: true,
      marketing: true,
      timestamp: new Date().toISOString(),
      version: COOKIE_CONSENT_VERSION,
    };
    setStoredConsent(consent);
    setIsVisible(false);
  };

  const handleDeclineAll = () => {
    const consent: CookieConsent = {
      essential: true, // Essential always enabled
      analytics: false,
      preferences: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: COOKIE_CONSENT_VERSION,
    };
    setStoredConsent(consent);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const consent: CookieConsent = {
      ...preferences,
      timestamp: new Date().toISOString(),
      version: COOKIE_CONSENT_VERSION,
    };
    setStoredConsent(consent);
    setIsVisible(false);
  };

  const togglePreference = (key: keyof typeof preferences) => {
    if (key === "essential") return; // Cannot disable essential
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[9999] w-[calc(100%-2rem)] sm:w-auto sm:max-w-md"
        >
          <div>
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              {/* Main content */}
              <div className="p-5 sm:p-6">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: 'var(--mininav-active-color, #14b8a6)' }}
                    >
                      <Cookie className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 font-satoshi">
                        Cookie Preferences
                      </h3>
                      <p className="text-sm text-gray-500">
                        We value your privacy
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleDeclineAll}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 leading-relaxed mb-5">
                  We use cookies to enhance your browsing experience, analyse site traffic, and personalise content. 
                  By clicking &quot;Accept All&quot;, you consent to our use of cookies. 
                  Read our{" "}
                  <Link 
                    href="/legal/cookies" 
                    className="underline hover:text-gray-900 transition-colors"
                    style={{ color: 'var(--mininav-active-color, #14b8a6)' }}
                  >
                    Cookie Policy
                  </Link>{" "}
                  for more details.
                </p>

                {/* Expandable details */}
                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-gray-100 pt-5 mb-5 space-y-3">
                        {/* Essential cookies */}
                        <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                          <div className="flex items-center gap-3">
                            <Shield className="w-4 h-4 text-gray-500" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">Essential Cookies</p>
                              <p className="text-xs text-gray-500">Required for site functionality</p>
                            </div>
                          </div>
                          <div className="px-3 py-1 rounded-full bg-gray-200 text-xs font-medium text-gray-600">
                            Always On
                          </div>
                        </div>

                        {/* Analytics cookies */}
                        <button
                          onClick={() => togglePreference("analytics")}
                          className="w-full flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3 text-left">
                            <div className="w-4 h-4 text-gray-500">📊</div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Analytics Cookies</p>
                              <p className="text-xs text-gray-500">Help us improve our website</p>
                            </div>
                          </div>
                          <div 
                            className={`w-11 h-6 rounded-full transition-colors relative ${
                              preferences.analytics ? "bg-teal-500" : "bg-gray-300"
                            }`}
                            style={preferences.analytics ? { backgroundColor: 'var(--mininav-active-color, #14b8a6)' } : {}}
                          >
                            <div 
                              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                                preferences.analytics ? "translate-x-5" : "translate-x-0.5"
                              }`}
                            />
                          </div>
                        </button>

                        {/* Preference cookies */}
                        <button
                          onClick={() => togglePreference("preferences")}
                          className="w-full flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3 text-left">
                            <div className="w-4 h-4 text-gray-500">⚙️</div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Preference Cookies</p>
                              <p className="text-xs text-gray-500">Remember your settings</p>
                            </div>
                          </div>
                          <div 
                            className={`w-11 h-6 rounded-full transition-colors relative ${
                              preferences.preferences ? "bg-teal-500" : "bg-gray-300"
                            }`}
                            style={preferences.preferences ? { backgroundColor: 'var(--mininav-active-color, #14b8a6)' } : {}}
                          >
                            <div 
                              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                                preferences.preferences ? "translate-x-5" : "translate-x-0.5"
                              }`}
                            />
                          </div>
                        </button>

                        {/* Marketing cookies */}
                        <button
                          onClick={() => togglePreference("marketing")}
                          className="w-full flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3 text-left">
                            <div className="w-4 h-4 text-gray-500">📢</div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Marketing Cookies</p>
                              <p className="text-xs text-gray-500">Personalised advertisements</p>
                            </div>
                          </div>
                          <div 
                            className={`w-11 h-6 rounded-full transition-colors relative ${
                              preferences.marketing ? "bg-teal-500" : "bg-gray-300"
                            }`}
                            style={preferences.marketing ? { backgroundColor: 'var(--mininav-active-color, #14b8a6)' } : {}}
                          >
                            <div 
                              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                                preferences.marketing ? "translate-x-5" : "translate-x-0.5"
                              }`}
                            />
                          </div>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    {showDetails ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Hide Options
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Customise
                      </>
                    )}
                  </button>
                  
                  <div className="flex-1" />
                  
                  <div className="flex items-center gap-3">
                    {showDetails ? (
                      <button
                        onClick={handleSavePreferences}
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                      >
                        Save Preferences
                      </button>
                    ) : (
                      <button
                        onClick={handleDeclineAll}
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                      >
                        Decline All
                      </button>
                    )}
                    <button
                      onClick={handleAcceptAll}
                      className="px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-colors shadow-lg hover:shadow-xl"
                      style={{ backgroundColor: 'var(--mininav-active-color, #14b8a6)' }}
                    >
                      Accept All
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Export helper functions for use elsewhere
export { getStoredConsent, COOKIE_CONSENT_KEY };
